import { Plus } from "lucide-react";

import type { ApiKeyDetail } from "@/api";
import { useTranslation } from "@/i18n";
import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";

interface ApiKeysQuickstartProps {
  onCreateClick: () => void;
}

const CURL_SAMPLE = `curl -X POST https://api.monago.id/v1/chat/completions \\
  -H "Authorization: Bearer sk-mng-..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gpt-4o-mini",
    "messages": [{"role": "user", "content": "Hi"}]
  }'`;

interface StepProps {
  index: number;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}

function Step({ index, active, title, children }: StepProps) {
  return (
    <div className="rounded-md border border-ocean-100 bg-white p-4">
      <div className="flex items-start gap-3">
        <div
          className={
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-medium " +
            (active
              ? "bg-ocean-600 text-white"
              : "bg-ocean-100 text-ocean-600")
          }
        >
          {index}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-ocean-900">{title}</div>
          <div className="mt-2 text-xs text-neutral-600">{children}</div>
        </div>
      </div>
    </div>
  );
}

export interface ApiKeysQuickstartItems {
  keys: ApiKeyDetail[];
}

/**
 * Empty-state quickstart for new tenants. Three steps:
 *   1. Buat API key (CTA back into the create dialog)
 *   2. Test dengan curl (copy-able snippet)
 *   3. Integrate ke sistem (security reminder)
 *
 * Renders only when ``keys.length === 0`` on the page.
 */
export function ApiKeysQuickstart({ onCreateClick }: ApiKeysQuickstartProps) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-ocean-100 bg-ocean-50/40 p-5 desktop:p-7">
      <h2 className="text-lg font-medium text-ocean-900">
        {t("apiKeys.quickstart.title")}
      </h2>
      <p className="mt-1 max-w-2xl text-sm text-neutral-600">
        {t("apiKeys.quickstart.description")}
      </p>

      <div className="mt-5 space-y-3">
        <Step index={1} active title={t("apiKeys.quickstart.step1Title")}>
          {t("apiKeys.quickstart.step1Body")}
          <div className="mt-3">
            <Button size="sm" onClick={onCreateClick}>
              <Plus size={14} />
              {t("apiKeys.quickstart.step1Button")}
            </Button>
          </div>
        </Step>

        <Step index={2} title={t("apiKeys.quickstart.step2Title")}>
          {t("apiKeys.quickstart.step2Body")}
          <div className="mt-3">
            <CodeBlock language="bash">{CURL_SAMPLE}</CodeBlock>
          </div>
        </Step>

        <Step index={3} title={t("apiKeys.quickstart.step3Title")}>
          {t("apiKeys.quickstart.step3Body")}
        </Step>
      </div>
    </div>
  );
}
